/**
 * 클립보드 복사 포맷.
 *
 * 제품 규칙: 클립보드에는 **인용과 원문만** 들어간다.
 * - `【관련 판례】` 같은 머리말, 안내문, 프롬프트 문구를 넣지 않는다.
 * - 마크다운/XML 태그를 넣지 않는다.
 * - 출력은 `\n`만 쓰고, 줄 끝 공백과 연속 빈 줄을 만들지 않는다.
 */

export interface CopyItem {
  kind: "statute" | "case";
  /** "민법 제750조" or "대법원 2024. 8. 13. 선고 2024다208865 판결" (already formatted). */
  citation: string;
  /** Optional heading/parenthetical: "불법행위의 내용" or "손해배상(기)". */
  title?: string;
  /** Verbatim sentence(s) from the source. */
  quote?: string;
  /** Optional location label, e.g. "판결이유 제3문장". */
  quoteLabel?: string;
}

/**
 * 인용문 내부 공백을 정리한다. 문단 구분(빈 줄)은 유지한다.
 *
 * - CRLF/CR은 LF로 바꾼다.
 * - 문단 안의 줄바꿈·연속 공백은 공백 하나로 접는다.
 * - 각 문단은 trim하고, 빈 문단은 버린다.
 * - 문단 사이는 정확히 빈 줄 하나(`\n\n`)로 잇는다.
 */
export function cleanQuote(text: string): string {
  const normalized = text.replace(/\r\n?/g, "\n");
  const paragraphs = normalized
    .split(/\n[ \t]*\n+/)
    .map((paragraph) => paragraph.replace(/\s+/g, " ").trim())
    .filter((paragraph) => paragraph.length > 0);
  return paragraphs.join("\n\n");
}

function label(value: string | undefined): string {
  return value?.trim() ?? "";
}

/** One item as plain text. */
export function formatCopyItem(item: CopyItem): string {
  const citation = item.citation.trim();
  const title = label(item.title);
  const suffix = title ? (item.kind === "case" ? ` [${title}]` : ` (${title})`) : "";
  const lines = [`${citation}${suffix}`];

  const quote = item.quote ? cleanQuote(item.quote) : "";
  if (quote) {
    const quoteLabel = label(item.quoteLabel);
    const quoted = item.kind === "case" ? `"${quote}"` : quote;
    lines.push(quoteLabel ? `${quoted} (${quoteLabel})` : quoted);
  }

  return lines.join("\n");
}

/** Multiple items, blank line between, no headers. */
export function formatCopyBlock(items: CopyItem[]): string {
  return items
    .map(formatCopyItem)
    .filter((text) => text.length > 0)
    .join("\n\n");
}
